import { twMerge } from "tailwind-merge";
import { Type, TypeVariant } from "@themes/pollinator/ui/Type";

export type Props = {
  /** Page title */
  title: string;
  /** Optional description */
  description?: React.ReactNode;
  /** Optional icon component */
  iconComponent?: React.ComponentType<{ className?: string }>;
  /** Additional CSS classes */
  className?: string;
  /** Additional CSS classes for the container */
  containerClassName?: string;
  /** Typography variant for the title */
  titleTypeVariant?: TypeVariant;
  /** Optional tabs */
  tabs?: React.ReactNode;
  /** Optional actions rendered on the right side of the tabs row */
  actions?: React.ReactNode;
};

/**
 * A page header component with title, optional description, and icon.
 */
export function AppPageHeader({
  title,
  titleTypeVariant = "h2",
  description,
  iconComponent,
  className,
  containerClassName,
  tabs,
  actions,
}: Props) {
  const Icon = iconComponent;

  return (
    <div
      className={twMerge(
        "mb-8 flex w-full flex-col items-center border-b border-border text-left md:sticky md:top-0 md:z-40 md:bg-brand-50 md:shadow-sm md:py-4",
        tabs ? "pb-0 md:pb-0" : "md:pb-6",
        containerClassName
      )}
    >
      <div
        className={twMerge(
          "flex w-full max-w-7xl flex-col px-4 py-4 md:px-8 md:py-0",
          tabs ? "gap-6 md:gap-8 pb-0" : "gap-1"
        )}
      >
        <div
          className={twMerge("flex flex-col gap-2", className)}
          data-testid="app-page-header"
        >
          <Type as="h1" variant={titleTypeVariant}>
            {title}
          </Type>
          {description && (
            <div className="flex flex-row gap-2 items-start">
              {Icon && <Icon className="w-4 h-4 shrink-0 relative top-1" />}
              <Type as="p" variant="body2" className="max-w-3xl">
                {description}
              </Type>
            </div>
          )}
        </div>
        {(tabs || actions) && (
          <div className={twMerge("relative -bottom-[1px] mt-auto flex w-full justify-between flex-col md:flex-row", actions ? "items-start gap-4 md:gap-0 md:flex-row-reverse" : "items-end")}>
            {actions && <div className="md:mb-2">{actions}</div>}
            {tabs && <div className="md:mt-2">{tabs}</div>}
          </div>
        )}
      </div>
    </div>
  );
}

export default AppPageHeader;
